import React, { useState, useMemo } from "react";
import { T } from "../design/tokens";
import { buildSplits, buildTrailSplits, buildNutritionPlan, buildTrailNutrition } from "../lib/trail";
import { secToHMS, timeToSec } from "../lib/utils";

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function parseObjectifSec(objectif) {
  if (!objectif) return null;
  return timeToSec(objectif);
}

/* ── Atom: Stat box ──────────────────────────────────────────────────────── */
function StatBox({ label, value, sub, color = T.navy }) {
  return (
    <div style={{ background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 8,
      padding: "12px 16px", flex: 1, minWidth: 90 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: "0.1em",
        textTransform: "uppercase", fontFamily: T.fontUI, marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: T.fontData, fontWeight: 700, fontSize: 20, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: T.textMid, marginTop: 3, fontFamily: T.fontUI }}>{sub}</div>}
    </div>
  );
}

/* ── Sub-tab: Pacing ─────────────────────────────────────────────────────── */
function PacingTab({ race, profile, targetSec, strategy, setStrategy }) {
  const fcMax   = profile?.fc_max   || 190;
  const fcRepos = profile?.fc_repos || 55;
  const isTrail = race.isTrail;

  const splits = useMemo(() => {
    if (!targetSec || targetSec <= 0) return [];
    if (isTrail && race.courseProfile?.denivele_plus) {
      // Build synthetic trail segments from course profile
      const cp = race.courseProfile;
      const dplus  = cp.denivele_plus  || 0;
      const dminus = cp.denivele_minus || 0;
      const dist   = race.distance;
      // Simple 3-segment model: first half climbs, middle flat, second half descends
      const segments = [
        { label: "Départ → Sommet", distKm: dist * 0.45, dplus: Math.round(dplus * 0.75), dminus: 0,   type: "montee"   },
        { label: "Crête",           distKm: dist * 0.10, dplus: Math.round(dplus * 0.25), dminus: 0,   type: "montee"   },
        { label: "Retour",          distKm: dist * 0.45, dplus: 0,   dminus: dminus, type: "descente" },
      ];
      return buildTrailSplits(segments, fcMax, fcRepos, profile?.vma || 14);
    }
    return buildSplits(race.distance, targetSec, strategy, fcMax, fcRepos);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [race, profile?.vma, profile?.fc_max, profile?.fc_repos, targetSec, strategy, isTrail]);

  const STRATEGIES = [
    { id: "even",     label: "Allure constante" },
    { id: "negative", label: "Negative split"   },
    { id: "positive", label: "Positive split"   },
  ];

  const ZONE_COLORS = { Z1: T.blue, Z2: T.green, Z3: "#CA8A04", Z4: T.orange, Z5: T.red };

  if (!targetSec) {
    return (
      <div style={{ background: T.surfaceSub, borderRadius: 10, padding: 32, textAlign: "center",
        border: `0.5px dashed ${T.border}` }}>
        <div style={{ fontSize: 13, color: T.textMid, fontFamily: T.fontUI }}>
          Renseigne un objectif chrono sur la course pour générer le plan de pace.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Strategy selector (road only) */}
      {!isTrail && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {STRATEGIES.map(s => (
            <button key={s.id} onClick={() => setStrategy(s.id)} style={{
              padding: "6px 14px", borderRadius: 6, border: `0.5px solid ${strategy === s.id ? T.navy : T.border}`,
              background: strategy === s.id ? T.navy : T.surface,
              color: strategy === s.id ? T.bg : T.textMid,
              fontSize: 12, fontFamily: T.fontUI, fontWeight: strategy === s.id ? 700 : 400, cursor: "pointer",
            }}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Splits table */}
      <div style={{ background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: T.fontUI }}>
            <thead>
              <tr style={{ background: T.surfaceSub, borderBottom: `0.5px solid ${T.border}` }}>
                {[isTrail ? "Segment" : "km", "Allure", "Elapsed", "FC", "Zone"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 11,
                    fontWeight: 600, color: T.textMid, letterSpacing: 0.3, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {splits.map((s, i) => (
                <tr key={i} style={{ borderBottom: `0.5px solid ${T.border}`,
                  background: i % 2 === 0 ? T.surface : T.surfaceSub + "44" }}>
                  <td style={{ padding: "9px 12px", fontWeight: 600, color: T.navy }}>
                    {isTrail ? (s.label || `Segment ${i + 1}`) : `${s.km} km`}
                  </td>
                  <td style={{ padding: "9px 12px", fontFamily: T.fontData, fontWeight: 700, color: T.navy, fontSize: 14 }}>
                    {s.pace || s.paceLabel || "--"}
                  </td>
                  <td style={{ padding: "9px 12px", fontFamily: T.fontData, color: T.textMid }}>
                    {s.elapsed}
                  </td>
                  <td style={{ padding: "9px 12px", fontFamily: T.fontData, color: T.textMid }}>
                    {s.fc} bpm
                  </td>
                  <td style={{ padding: "9px 12px" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: ZONE_COLORS[s.zone?.z] || T.textMid,
                      background: (ZONE_COLORS[s.zone?.z] || T.textMid) + "14",
                      padding: "2px 8px", borderRadius: 4 }}>
                      {s.zone?.z} · {s.zone?.label}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-tab: Nutrition ──────────────────────────────────────────────────── */
function NutritionTab({ race, targetSec }) {
  const [tempChaud, setTempChaud] = useState(false);

  const plan = useMemo(() => {
    if (!targetSec || targetSec <= 0) return [];
    if (race.isTrail) return buildTrailNutrition(race.distance, targetSec, 3, tempChaud);
    return buildNutritionPlan(race.distance, targetSec);
  }, [race, targetSec, tempChaud]);

  const TYPE_COLORS = {
    repas:  T.textMid, hydra: T.blue, gel:    T.orange, barre: "#92400e",
    fruit:  "#b45309", sel:   "#6b7280", chaleur: T.red, arrivee: T.green, prep: T.textMid,
  };

  if (!targetSec) {
    return (
      <div style={{ background: T.surfaceSub, borderRadius: 10, padding: 32, textAlign: "center",
        border: `0.5px dashed ${T.border}` }}>
        <div style={{ fontSize: 13, color: T.textMid, fontFamily: T.fontUI }}>
          Renseigne un objectif chrono pour générer le plan nutritionnel.
        </div>
      </div>
    );
  }

  return (
    <div>
      {race.isTrail && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
          background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 8, padding: "10px 14px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
            fontSize: 13, fontFamily: T.fontUI, color: T.navy }}>
            <input type="checkbox" checked={tempChaud} onChange={e => setTempChaud(e.target.checked)}
              style={{ width: 15, height: 15, accentColor: T.orange }} />
            Conditions chaudes (T° &gt; 22°C) — adapte les intervalles et le protocole sel
          </label>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {plan.map((item, i) => {
          const color = TYPE_COLORS[item.type] || T.textMid;
          const isSpecial = item.type === "arrivee" || item.type === "prep";
          return (
            <div key={i} style={{
              display: "flex", gap: 12, padding: "10px 14px",
              background: isSpecial ? `${color}0E` : T.surface,
              border: `0.5px solid ${isSpecial ? color + "40" : T.border}`,
              borderLeft: `3px solid ${color}`,
              borderRadius: 8,
            }}>
              <div style={{ flexShrink: 0, textAlign: "center", minWidth: 44 }}>
                <div style={{ fontSize: 18, lineHeight: 1 }}>{item.icon}</div>
                {item.km >= 0 && item.km !== undefined && (
                  <div style={{ fontSize: 10, fontFamily: T.fontData, fontWeight: 700, color: T.textLight,
                    marginTop: 2 }}>km {item.km}</div>
                )}
                <div style={{ fontSize: 10, fontFamily: T.fontData, color: T.textLight }}>{item.time}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 12, color, fontFamily: T.fontUI }}>{item.label}</div>
                <div style={{ fontSize: 12, color: T.textMid, marginTop: 3, lineHeight: 1.5,
                  fontFamily: T.fontUI }}>{item.detail}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Sub-tab: Crew ───────────────────────────────────────────────────────── */
function CrewPlanTab({ race, targetSec, book }) {
  const checkpoints = book?.crewCheckpoints || [];

  if (checkpoints.length === 0) {
    return (
      <div style={{ background: T.surfaceSub, borderRadius: 10, padding: 32, textAlign: "center",
        border: `0.5px dashed ${T.border}` }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>👥</div>
        <div style={{ fontWeight: 600, fontSize: 14, color: T.navy, fontFamily: T.fontUI }}>
          Aucun point crew défini
        </div>
        <div style={{ fontSize: 12, color: T.textMid, marginTop: 8, fontFamily: T.fontUI, maxWidth: 400, margin: "8px auto 0" }}>
          Renseigne les points de passage crew dans l'onglet Race Book → Crew pour les voir apparaître ici avec les temps estimés.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: T.goldLight, border: `0.5px solid ${T.gold}40`, borderRadius: 8,
        padding: "10px 14px", marginBottom: 16, fontSize: 12, color: T.navy, fontFamily: T.fontUI }}>
        💡 Les temps estimés ci-dessous sont calculés depuis l'objectif de l'athlète.
        Ajoutez 5–10% de marge de sécurité pour le crew.
      </div>
      {checkpoints.map((cp, i) => {
        const estimatedSec = targetSec ? (cp.km / race.distance) * targetSec : null;
        return (
          <div key={i} style={{ display: "flex", gap: 14, padding: "14px 16px", marginBottom: 8,
            background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 8 }}>
            <div style={{ textAlign: "center", flexShrink: 0, minWidth: 52 }}>
              <div style={{ fontFamily: T.fontData, fontWeight: 700, fontSize: 11, color: T.textLight,
                letterSpacing: "0.06em", textTransform: "uppercase" }}>km</div>
              <div style={{ fontFamily: T.fontData, fontWeight: 700, fontSize: 24, color: T.gold,
                lineHeight: 1 }}>{cp.km}</div>
              {estimatedSec && (
                <div style={{ fontFamily: T.fontData, fontWeight: 600, fontSize: 12, color: T.textMid,
                  marginTop: 2 }}>{secToHMS(estimatedSec)}</div>
              )}
            </div>
            <div style={{ flex: 1, borderLeft: `2px solid ${T.border}`, paddingLeft: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: T.navy, fontFamily: T.fontUI,
                marginBottom: 4 }}>{cp.label}</div>
              {cp.timing && (
                <div style={{ fontSize: 12, color: T.textMid, marginBottom: 3 }}>
                  ⏱ {cp.timing}
                </div>
              )}
              {cp.accessNotes && (
                <div style={{ fontSize: 12, color: T.textMid, fontStyle: "italic",
                  padding: "6px 8px", background: T.surfaceSub, borderRadius: 5 }}>
                  {cp.accessNotes}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
export default function TabRacePlan({ races = [], profile = {}, onUpdateRace }) {
  // Only races with GPX attached and not yet finished
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const eligibleRaces = races.filter(r => {
    if (!r.gpxData || r.statut === "brouillon" || r.statut === "terminé") return false;
    if (!r.date) return true;
    const [y, m, d] = r.date.split("-").map(Number);
    return new Date(y, m - 1, d) >= today;
  }).sort((a, b) => (a.date ?? "") > (b.date ?? "") ? 1 : -1);

  const [selectedId, setSelectedId] = useState(eligibleRaces[0]?.id ?? null);
  const [subTab,     setSubTab]     = useState("pacing");
  const [strategy,   setStrategy]   = useState("even");

  const race      = eligibleRaces.find(r => r.id === selectedId) ?? eligibleRaces[0] ?? null;
  const targetSec = race ? parseObjectifSec(race.objectif) : null;
  const book      = race?.trail_data?.book ?? null;

  const SUB_TABS = [
    { id: "pacing",    label: "Pacing" },
    { id: "nutrition", label: "Nutrition" },
    { id: "crew",      label: "Crew" },
  ];

  if (!race) {
    const hasRacesNoGpx = races.some(r => !r.gpxData && r.statut !== "terminé");
    return (
      <div style={{ textAlign: "center", padding: 60, fontFamily: T.fontUI }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>{hasRacesNoGpx ? "📁" : "📋"}</div>
        <div style={{ fontWeight: 700, fontSize: 16, color: T.navy }}>
          {hasRacesNoGpx ? "GPX manquant" : "Aucune course renseignée"}
        </div>
        <div style={{ fontSize: 13, color: T.textMid, marginTop: 8 }}>
          {hasRacesNoGpx
            ? "Le Race Plan nécessite un fichier GPX attaché à ta course. Ajoute-le depuis l'onglet Planning."
            : "Ajoute une course dans l'onglet Planning pour préparer ton plan de course."}
        </div>
      </div>
    );
  }

  const pacePerKm = targetSec ? secToHMS(targetSec / race.distance) : null;
  const avgFC     = profile?.fc_max && profile?.fc_repos
    ? Math.round(profile.fc_repos + (profile.fc_max - profile.fc_repos) * 0.80)
    : null;

  return (
    <div style={{ fontFamily: T.fontUI }}>
      {/* Race selector + stats */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.textLight, letterSpacing: "0.1em",
            textTransform: "uppercase", marginBottom: 4 }}>Plan de Course</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.navy, lineHeight: 1.2 }}>
            {race.nom}
          </h1>
          <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: T.textMid }}>{race.distance} km</span>
            {race.courseProfile?.denivele_plus && (
              <span style={{ fontSize: 12, color: T.green }}>D+ {race.courseProfile.denivele_plus}m</span>
            )}
            {race.objectif && (
              <span style={{ fontFamily: T.fontData, fontWeight: 700, fontSize: 14, color: T.gold }}>
                🎯 {race.objectif}
              </span>
            )}
          </div>
        </div>
        <select value={selectedId ?? ""} onChange={e => { setSelectedId(+e.target.value || e.target.value); setSubTab("pacing"); }}
          style={{ padding: "7px 12px", borderRadius: 7, border: `0.5px solid ${T.border}`,
            background: T.surface, fontSize: 12, color: T.navy, fontFamily: T.fontUI, cursor: "pointer",
            alignSelf: "flex-start" }}>
          {eligibleRaces.map(r => (
            <option key={r.id} value={r.id}>{r.nom} — {r.distance}km</option>
          ))}
        </select>
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        <StatBox label="Objectif"   value={race.objectif || "—"} color={T.gold} />
        <StatBox label="Allure/km"  value={pacePerKm ? `${pacePerKm}/km` : "—"} color={T.navy} />
        {race.distance && <StatBox label="Distance"   value={`${race.distance} km`} color={T.blue} />}
        {race.courseProfile?.denivele_plus && (
          <StatBox label="D+"        value={`${race.courseProfile.denivele_plus}m`} color={T.green} />
        )}
        {avgFC && <StatBox label="FC cible" value={`${avgFC} bpm`} color={T.orange} sub="~80% FCR" />}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: "flex", borderBottom: `0.5px solid ${T.border}`, marginBottom: 20 }}>
        {SUB_TABS.map(st => (
          <button key={st.id} onClick={() => setSubTab(st.id)} style={{
            padding: "9px 16px", border: "none", background: "none", cursor: "pointer",
            fontSize: 12, fontFamily: T.fontUI, fontWeight: subTab === st.id ? 700 : 400,
            color: subTab === st.id ? T.navy : T.textMid,
            borderBottom: `2px solid ${subTab === st.id ? T.gold : "transparent"}`,
            borderTop: "2px solid transparent", transition: "color 0.12s, border-color 0.12s",
          }}>
            {st.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {subTab === "pacing"    && (
        <PacingTab race={race} profile={profile} targetSec={targetSec}
          strategy={strategy} setStrategy={setStrategy} />
      )}
      {subTab === "nutrition" && <NutritionTab race={race} targetSec={targetSec} />}
      {subTab === "crew"      && <CrewPlanTab  race={race} targetSec={targetSec} book={book} />}
    </div>
  );
}
